import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Settings2 } from "lucide-react";
import { API, adminApi } from "./AdminDashboard";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMES = (() => {
  const out = [];
  for (let h = 6; h <= 20; h++) {
    for (const m of [0, 30]) {
      const hour12 = ((h + 11) % 12) + 1;
      out.push(`${hour12}:${m === 0 ? "00" : "30"} ${h < 12 ? "AM" : "PM"}`);
    }
  }
  return out;
})();

const inputCls = "w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-2.5 text-sm text-[#1A2F24] focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pantry/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const toggleDay = (d) => setSettings((s) => ({
    ...s,
    open_days: s.open_days.includes(d) ? s.open_days.filter((x) => x !== d) : [...s.open_days, d],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi().put(`${API}/admin/pantry/settings`, {
        open_days: settings.open_days,
        start_time: settings.start_time,
        end_time: settings.end_time,
        slot_minutes: settings.slot_minutes,
        max_per_slot: settings.max_per_slot,
      });
      setSettings(data);
      toast.success("Pantry settings saved. The booking form updated instantly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-sm text-[#5C7368]">Loading settings…</p>;

  return (
    <div className="max-w-2xl rounded-2xl border border-[#E2E8E4] bg-[#F2F4F0] p-7" data-testid="admin-settings-tab">
      <div className="flex items-center gap-3">
        <Settings2 size={20} className="text-[#F4A261]" />
        <h3 className="font-display text-2xl text-[#1A2F24]">Pantry Settings</h3>
      </div>
      <p className="mt-2 text-sm text-[#5C7368]">Control which days the pantry is open, visiting hours, appointment length, and how many families can book each slot.</p>

      <p className="mt-6 text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-2">Open Days</p>
      <div className="flex flex-wrap gap-2" data-testid="settings-open-days">
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => toggleDay(i)} data-testid={`settings-day-${d.toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-xs tracking-wide transition-all duration-300 ${
              settings.open_days.includes(i) ? "bg-[#1E352A] text-[#FAF9F6]" : "bg-white/70 border border-[#E2E8E4] text-[#5C7368] hover:border-[#F4A261]"
            }`}>
            {d}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-2">Opens At</p>
          <select value={settings.start_time} onChange={(e) => setSettings({ ...settings, start_time: e.target.value })} data-testid="settings-start-time" className={inputCls}>
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-2">Closes At</p>
          <select value={settings.end_time} onChange={(e) => setSettings({ ...settings, end_time: e.target.value })} data-testid="settings-end-time" className={inputCls}>
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-2">Appointment Length</p>
          <select value={settings.slot_minutes} onChange={(e) => setSettings({ ...settings, slot_minutes: Number(e.target.value) })} data-testid="settings-slot-minutes" className={inputCls}>
            {[15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{m} minutes</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C7368] mb-2">Families Per Slot</p>
          <input type="number" min="1" max="100" value={settings.max_per_slot} onChange={(e) => setSettings({ ...settings, max_per_slot: Number(e.target.value) || 1 })} data-testid="settings-max-per-slot" className={inputCls} />
        </div>
      </div>

      <p className="mt-5 text-xs text-[#5C7368]" data-testid="settings-preview">
        Current slots: {settings.slots?.slice(0, 4).join(", ")}{settings.slots?.length > 4 ? ` … (${settings.slots.length} slots)` : ""}
      </p>

      <button onClick={save} disabled={saving} data-testid="settings-save-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-6 py-2.5 text-xs tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Settings
      </button>
    </div>
  );
};

export default SettingsTab;
